<div class="">
  <p-toast></p-toast>
  <p-confirmDialog></p-confirmDialog>

  @if (loading()) {
    <div class="flex items-center justify-center py-8">
      <i class="pi pi-spin pi-spinner text-3xl"></i>
    </div>
  } @else {
    <form
      class="flex flex-col gap-6"
      (ngSubmit)="onSubmit()"
      #parkingRequestForm="ngForm"
    >
      <!-- Booking Selection -->
      <app-parking-request-booking-field
        [readonly]="readonlyState().booking"
        [bookings]="filteredBookings()"
        [loadingMoreBookings]="loadingMoreBookings()"
        [loading]="bookingLoading()"
        [initialValue]="bookingValue"
        [selectedBookingObject]="selectedBookingObject"
        (bookingChange)="onBookingChange($event)"
        (bookingSearchChange)="onBookingSearchChange($event)"
        (bookingScrollToBottom)="onBookingScrollToBottom()"
      ></app-parking-request-booking-field>

      <!-- Date Range -->
      <app-parking-request-date-fields
        #dateFieldsComponent
        [startDateReadonly]="readonlyState().startDate"
        [endDateReadonly]="readonlyState().endDate"
        [initialStartDate]="startDateValue"
        [initialEndDate]="endDateValue"
        (startDateChange)="onStartDateChange($event)"
        (endDateChange)="onEndDateChange($event)"
      ></app-parking-request-date-fields>

      <!-- General date corrections message -->
      @if (dateCorrectionsMessage()) {
        <div class="mt-2 rounded-md border border-blue-200 bg-blue-50 p-3">
          <div class="flex items-start">
            <i class="pi pi-info-circle mr-2 mt-0.5 text-blue-600"></i>
            <div class="text-sm text-blue-800">
              <strong>{{ 'parking.form.datesAutomaticallyAdjusted' | translate }}:</strong>
              <p class="mt-1">{{ dateCorrectionsMessage() }}</p>
            </div>
          </div>
        </div>
      }

      <!-- Parking Place Selection -->
      <app-parking-request-parking-place-field
        [readonly]="readonlyState().parkingPlace"
        [parkingPlaceOptions]="parkingPlaceOptions()"
        [initialValue]="parkingPlaceValue"
        [selectedParkingPlaceObject]="selectedParkingPlaceObject"
        (parkingPlaceChange)="onParkingPlaceChange($event)"
      ></app-parking-request-parking-place-field>

      <!-- Checkbox Options -->
      <div class="flex flex-col gap-6 md:flex-row">
        <!-- Disabled Parking Place -->
        <div class="field-checkbox flex items-center">
          <p-checkbox
            [(ngModel)]="disabledParkingPlaceValue"
            name="disabledParkingPlace"
            #disabledParkingPlaceRef="ngModel"
            [ngModelOptions]="{ standalone: true }"
            (ngModelChange)="onDisabledParkingPlaceChange($event)"
            [binary]="true"
            inputId="disabled_parking_place"
          >
          </p-checkbox>
          <label
            for="disabled_parking_place"
            class="ml-2"
          >
            {{ 'parking.form.disabledParkingPlace' | translate }}
          </label>
        </div>

        <!-- Paid Status -->
        <div class="field-checkbox flex items-center">
          <p-checkbox
            [(ngModel)]="paidValue"
            name="paid"
            #paidRef="ngModel"
            [ngModelOptions]="{ standalone: true }"
            (ngModelChange)="onPaidChange($event)"
            [binary]="true"
            inputId="paid"
          >
          </p-checkbox>
          <label
            for="paid"
            class="ml-2"
          >
            {{ 'parking.form.paid' | translate }}
          </label>
        </div>
      </div>

      <!-- License Plate Information -->
      <div class="field-checkbox mb-4 flex items-center">
        <p-checkbox
          [(ngModel)]="licensePlateNotKnownValue"
          name="licensePlateNotKnown"
          #licensePlateNotKnownRef="ngModel"
          [ngModelOptions]="{ standalone: true }"
          (ngModelChange)="onLicensePlateNotKnownChange($event)"
          [binary]="true"
          inputId="license_plate_not_known"
        >
        </p-checkbox>
        <label
          for="license_plate_not_known"
          class="ml-2"
        >
          {{ 'parking.form.licensePlateNotKnown' | translate }}
        </label>
      </div>

      <!-- License Plate Fields -->
      @if (!licensePlateNotKnownValue) {
        <div class="flex flex-col gap-4 md:flex-row">
          <!-- License Plate -->
          <div class="field flex-1">
            <label class="mb-2 block font-medium">{{ 'parking.form.licensePlate' | translate }}</label>
            <input
              pInputText
              [(ngModel)]="licensePlateValue"
              name="licensePlate"
              #licensePlateRef="ngModel"
              [ngModelOptions]="{ standalone: true }"
              (ngModelChange)="onLicensePlateChange($event)"
              class="w-full"
              [placeholder]="'parking.form.enterLicensePlate' | translate"
            />
          </div>

          <!-- Country Code -->
          <div class="field flex-1">
            <label class="mb-2 block font-medium">{{ 'parking.form.country' | translate }}</label>
            <app-country-select
              [value]="licensePlateCountryCodeValue"
              (valueChange)="onLicensePlateCountryCodeChange($event)"
              [placeholder]="'parking.form.selectCountry' | translate"
              [styleClass]="'w-full'"
              [showClear]="true"
            >
            </app-country-select>
          </div>
        </div>
      }

      <!-- Form Actions -->
      <div class="flex justify-between gap-2 pt-4">
        <!-- Delete button (only in edit mode) -->
        @if (editMode()) {
          <button
            pButton
            type="button"
            label="{{ 'common.delete' | translate }}"
            icon="pi pi-trash"
            class="p-button-danger"
            [disabled]="submitting()"
            (click)="onDelete()"
          ></button>
        } @else {
          <div></div>
        }

        <!-- Right-aligned action buttons -->
        <div class="flex gap-2">
          <button
            pButton
            type="button"
            label="{{ 'common.reset' | translate }}"
            class="p-button-secondary"
            [disabled]="editMode() ? !hasChanges() : false"
            (click)="onReset()"
          ></button>
          <button
            pButton
            type="button"
            label="{{ 'common.cancel' | translate }}"
            class="p-button-outlined"
            (click)="onCancel()"
          ></button>
          <button
            pButton
            type="submit"
            label="{{ editMode() ? ('common.update' | translate) : ('common.create' | translate) }}"
            [loading]="submitting()"
            [disabled]="parkingRequestForm.invalid || submitting() || !isFormValid() || (editMode() && !hasChanges())"
          ></button>
        </div>
      </div>
    </form>
  }
</div>
